import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { post } from "../../util/axiosLib";
import bankApi from "../../services/bankApi";
import ls from "util/secureStorage";
import constant from "config/constant";
import { wrapBaseApi } from "util/apiWrapper";

const getToken = () => ls.get(constant.AUTH_TOKEN_KEY).token;

const getHeaders = () => ({
  authorization: getToken(),
  "Content-Type": "application/json",
});

const initialState = {
  status: "idle",
  level2: {
    files: [],
    status: "idle",
    docType: null,
    docLink: null,
    category: null,
    supportingDocType: null,
    supportingDocLink: null,
    individualLink: null,

    docName: null,
    nameOnId: null,
    ninNumber: null,
    dateOfBirth: null,
    // kraPin: null,
  },

  level3: {
    status: "idle",
    bankName: null,
    bankCode: null,
    country: null,
    branchName: null,
    bankAccountName: null,
    bankAccountNumber: null,
    link: null,

    address: null,
    streetNumber: null,
    city: null,
    zipCode: null,
    bankStatementLink: null,
    state: null,
  },

  error: {
    hasError: false,
    errorCode: null,
    message: null,
  },
};

export const uploadLevel2Docs = createAsyncThunk(
  "accountVerification/uploadLevel2Docs",
  async (_, { getState, rejectWithValue }) => {
    const {
      accountVerification: { level2 },
    } = getState();
    try {
      const response = await post(
        wrapBaseApi(`/verification/upload`),
        { ...level2 },
        getHeaders()
      );

      console.log("RESPONSE", response);
      return response?.data;
    } catch (error) {
      console.log("LEVEL 2 UPLOAD CALL", error);
      return rejectWithValue(error);
    }

    // return response.data;
  }
);

export const uploadLevel3Docs = createAsyncThunk(
  "accountVerification/uploadLevel3Docs",
  async (_, { getState, rejectWithValue }) => {
    const {
      accountVerification: { level3 },
    } = getState();

    try {
      const countryCodeMap = {
        ng: "nigeria",
        gh: "ghana",
        ke: "kenya",
      };
      const request = {
        withoutBankId: async () =>
          await post(
            wrapBaseApi(`/account/addBankDetails`),
            { ...level3 },
            getHeaders()
          ),
        withBankId: async (bankId, country) =>
          await post(
            wrapBaseApi(`/verification/upload/level-three`),
            {
              ...level3,
              branchName: level3?.branchName ?? "",
              country: countryCodeMap[country],
            },
            getHeaders()
          ),
      };

      // console.log("LEVEL 3 BANK ID", level3);

      const response =
        level3?.useBankId || !!level3?.bankId
          ? await request["withBankId"](level3.bankId, level3.country)
          : await request["withoutBankId"]();

      // console.log("RESPONSE", response);
      return response.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

const accountVerificationSlice = createSlice({
  name: "accountVerification",
  initialState,
  reducers: {
    updateLevel2Form: (state, action) => {
      state.level2 = { ...state.level2, ...action.payload };
    },
    updateLevel3Form: (state, action) => {
      state.level3 = { ...state.level3, ...action.payload };
    },
    clearError: (state, _) => {
      state.error = { ...initialState.error };
    },
    resetLevel2Form: (state, _) => {
      state.level2 = { ...initialState.level2 };
    },
    resetLevel3Form: (state, _) => {
      state.level3 = { ...initialState.level3 };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(uploadLevel2Docs.pending, (state, _) => {
      state.level2.status = "loading";
    });
    builder.addCase(uploadLevel2Docs.fulfilled, (state, _) => {
      state.level2.status = "success";
      bankApi.endpoints.getLevel2Status.initiate(_, {
        subscribe: false,
        forceRefetch: true,
      });
    });
    builder.addCase(uploadLevel2Docs.rejected, (state, action) => {
      state.level2.status = "error";
      state.error.hasError = true;
      state.error.errorCode = action.payload?.data?.errorCode;
      state.error.message = action.payload?.error;
      console.log("LEVEL 2 ERROR MESSAGE", action.error);
    });
    builder.addCase(uploadLevel3Docs.pending, (state, _) => {
      state.level3.status = "loading";
    });
    builder.addCase(uploadLevel3Docs.fulfilled, (state, _) => {
      state.level3.status = "success";
      bankApi.endpoints.getBanksDetails.initiate(_, {
        subscribe: false,
        forceRefetch: true,
      });
    });
    builder.addCase(uploadLevel3Docs.rejected, (state, action) => {
      state.level3.status = "error";
      state.error.errorCode = action.payload?.data?.errorCode;
      state.error.message = action.payload?.error;
    });
  },
});

export const selectAccountVerification = (state) => state.accountVerification;

export const { actions, reducer: AccountVerificationReducer } =
  accountVerificationSlice;
export const {
  updateLevel2Form,
  updateLevel3Form,
  clearError,
  resetLevel2Form,
  resetLevel3Form,
} = actions;
